import { useState, useCallback } from "react"
import { usePage } from "@inertiajs/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import { TurnstileWidget } from "./TurnstileWidget"
import { getCsrfToken } from "../utils"
import type { SharedProps } from "../types"

interface ReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reportUrl: string
}

export function ReportDialog({
  open,
  onOpenChange,
  reportUrl,
}: ReportDialogProps) {
  const { turnstile_site_key } = usePage().props as unknown as SharedProps

  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const handleVerify = useCallback((token: string) => {
    setTurnstileToken(token)
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErrors([])
    setIsSubmitting(true)

    try {
      const res = await fetch(reportUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email,
          message,
          cf_turnstile_response: turnstileToken,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        toast.success(data.message || "Thank you for your report.")
        onOpenChange(false)
        setEmail("")
        setMessage("")
        setTurnstileToken("")
      } else {
        const data = await res.json()
        setErrors(data.errors || [data.error || "Something went wrong."])
      }
    } catch {
      setErrors(["Network error. Please try again."])
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Suggest Edit</DialogTitle>
          <DialogDescription>
            Is there any new information you would like to share about this
            coffee shop? Or maybe you want to correct any information displayed?
            Let us know.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div className="rounded-md bg-red-50 p-3">
              <div className="text-sm text-red-700 space-y-1">
                {errors.map((err, i) => (
                  <p key={i}>{err}</p>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="report-email">Email (optional)</Label>
            <Input
              id="report-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-message">Message</Label>
            <Textarea
              id="report-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What needs to be changed?"
              required
              minLength={10}
              rows={8}
            />
          </div>

          {turnstile_site_key && (
            <TurnstileWidget
              siteKey={turnstile_site_key}
              onVerify={handleVerify}
            />
          )}

          <DialogFooter className="flex-row sm:justify-between gap-2">
            <Button
              type="button"
              asChild
              className="bg-[#0088cc] hover:bg-[#0077b3] text-white"
            >
              <a
                href="https://t.me/petakopi"
                target="_blank"
                rel="noopener noreferrer"
              >
                Report in Telegram
              </a>
            </Button>
            <Button type="submit" disabled={isSubmitting || !turnstileToken}>
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
